/**
 * Helpers for writes that go through pam-deacon's single writer
 * (apps/common/writer_client.py, apps/writer).
 *
 * MODIFIED_DATE is the optimistic-concurrency token. Every row a save sends
 * carries the MODIFIED_DATE it was loaded with, exactly as the API returned it
 * (null for a row never saved). The writer rejects the whole batch with
 * 409 `version_conflict` when any key has moved on, and stamps MODIFIED_BY and
 * the new MODIFIED_DATE itself. Never retry a 409 with a refreshed token: that
 * is a blind overwrite. Reload instead.
 */

export const MODIFIED_DATE_FIELD = 'MODIFIED_DATE';

// Both texts match writer_client.py's CONFLICT_MESSAGE / OUTCOME_UNKNOWN_MESSAGE.
export const VERSION_CONFLICT_MESSAGE =
  'Someone else saved these rows after you loaded them. Reload to see their changes, then re-apply yours.';

export const OUTCOME_UNKNOWN_MESSAGE =
  'The save service did not confirm your save, so it may or may not have gone through. Reload to check before saving again.';

// Fields the writer stamps itself (MODIFIED_BY) or no longer accepts (VERSION).
const NOT_SENT = ['VERSION', 'MODIFIED_BY'];

const GATEWAY_STATUSES = [502, 503, 504];

// The SWA Function's own answer when the App Service hop threw: the request may
// already have reached the backend and committed.
const PROXY_ERROR = 'proxy_error';

const responseBody = (error) => {
  const data = error?.response?.data;
  return data && typeof data === 'object' ? data : {};
};

const nonEmptyString = (value) => (typeof value === 'string' && value.trim() !== '' ? value : null);

/** The MODIFIED_DATE `row` was loaded with, untouched; null for a never-saved row. */
export const loadedModifiedDate = (row) => {
  const value = row?.[MODIFIED_DATE_FIELD];
  if (value === undefined || value === null) return null;
  return typeof value === 'string' && value.trim() === '' ? null : value;
};

/**
 * `row` as the writer expects it: carrying the MODIFIED_DATE of `loadedRow`
 * (default: `row` itself), never its own, and without VERSION / MODIFIED_BY.
 */
export const withLoadedModifiedDate = (row, loadedRow = row) => {
  const payload = { ...row };
  NOT_SENT.forEach((field) => delete payload[field]);
  return { ...payload, [MODIFIED_DATE_FIELD]: loadedModifiedDate(loadedRow) };
};

/** True for the writer's 409 `version_conflict` (not for an app's own 409s). */
export const isVersionConflict = (error) =>
  error?.response?.status === 409 && responseBody(error).error === 'version_conflict';

/**
 * True when the write may or may not have committed: the request was sent but
 * no response came back, the Function's 502 `proxy_error`, a gateway
 * 502/503/504 that is not the app's own error, or the backend's outcome-unknown
 * 503. That last one carries no machine-readable code, so its text is matched.
 */
export const isOutcomeUnknown = (error) => {
  const response = error?.response;
  if (!response) {
    return Boolean(error?.request) || ['ECONNABORTED', 'ETIMEDOUT'].includes(error?.code);
  }
  const appError = responseBody(error).error;
  if (response.status === 502 && appError === PROXY_ERROR) return true;
  if (response.status === 503 && appError === OUTCOME_UNKNOWN_MESSAGE) return true;
  return GATEWAY_STATUSES.includes(response.status) && !appError;
};

/** Reload after any 409 (the rows on screen are stale) or an unknown outcome. */
export const shouldReloadAfterWriteError = (error) =>
  error?.response?.status === 409 || isOutcomeUnknown(error);

/**
 * The message to show for a failed write: the conflict message, the
 * outcome-unknown message, the app's own `message` / `error` text, or
 * `fallback`. A 500's text is only "Internal server error", so the fallback
 * reads better there.
 */
export const getWriteErrorMessage = (error, fallback) => {
  const body = responseBody(error);
  if (isVersionConflict(error)) return nonEmptyString(body.message) || VERSION_CONFLICT_MESSAGE;
  if (isOutcomeUnknown(error)) return OUTCOME_UNKNOWN_MESSAGE;
  if (error?.response && error.response.status !== 500) {
    const appText = nonEmptyString(body.message) || nonEmptyString(body.error);
    if (appText) return appText;
  }
  return fallback;
};
